
































































































































































































































































import Vue from 'vue'
import Component from 'vue-class-component'
import appManager from '@/services/AppManager'
import * as api from '@/services/ApiService'
import Spinner from '@/components/Spinner.vue'
import {
  AirMover,
  AirScrubber,
  AirScrubberSettingData,
  Dehumidifier,
  Equipment,
  EquipmentType,
  FloorDryingSystem,
  Heater,
  HeaterSettingData
} from '@/core/EquipmentTypes'
import { AppColors } from '@/model/AppColors'
import { getEquipmentSettingText } from '@/core/EquipmentUtils'

interface TableHeader {
  text: string
  value: string
  align: string
}

interface AirScrubberSettingPair {
  airScrubber: AirScrubber
  airScrubberSetting: AirScrubberSettingData
}

interface HeaterSettingPair {
  heater: Heater
  heaterSetting: HeaterSettingData
}

@Component({
  components: {
    Spinner
  }
})
export default class EquipmentView extends Vue {
  isLoadingEquipment: boolean = false
  equipmentList: Equipment[] = []
  equipmentListSnapshotCounter: number = 0 // simple way to force a refresh when new items arise
  airMovers: AirMover[] = []
  airScrubbers: AirScrubber[] = []
  dehumidifiers: Dehumidifier[] = []
  floorDryingSystems: FloorDryingSystem[] = []
  heaters: Heater[] = []
  displayEquipmentTypeIndex: number = 0 // 0=AM, 1=AS, 2=FDS, 3=DH, 4=HTR

  airMoverHeaders: TableHeader[] = [
    { text: 'Manufacturer', value: 'data.manufacturer.name', align: 'start' },
    { text: 'Model', value: 'data.model', align: 'start' },
    { text: 'Type', value: 'data.airMoverType', align: 'start' },
    { text: 'Calc Room Type', value: 'data.calcRoomCountsType', align: 'start' },
    { text: 'Volts', value: 'data.volts', align: 'end' },
    { text: 'Amps', value: 'data.amps', align: 'end' },
  ]
  dehumidifierHeaders: TableHeader[] = [
    { text: 'Manufacturer', value: 'data.manufacturer.name', align: 'start' },
    { text: 'Model', value: 'data.model', align: 'start' },
    { text: 'Type', value: 'data.dehumidifierType', align: 'start' },
    { text: 'Pints', value: 'data.pints', align: 'end' },
    { text: 'Volts', value: 'data.volts', align: 'end' },
    { text: 'Amps', value: 'data.amps', align: 'end' },
  ]

  // noinspection JSUnusedGlobalSymbols
  async created() {
    this.isLoadingEquipment = true

    try {
      const response = await api.queryEquipment({})
      for (const equipment of response.equipment) {
        switch (equipment.data.equipmentType) {
          case EquipmentType.AirMover:
            this.airMovers.push(equipment as AirMover)
            break;
          case EquipmentType.AirScrubber:
            this.airScrubbers.push(equipment as AirScrubber)
            break;
          case EquipmentType.Dehumidifier:
            this.dehumidifiers.push(equipment as Dehumidifier)
            break;
          case EquipmentType.FloorDryingSystem:
            this.floorDryingSystems.push(equipment as FloorDryingSystem)
            break;
          case EquipmentType.Heater:
            this.heaters.push(equipment as Heater)
            break;
        }
        this.equipmentList.push(equipment)
      }

      // sort by manufacturer, then model
      this.airMovers = this.airMovers.sort(EquipmentView.compareEquirementManufacturerThenModel)
      this.airScrubbers = this.airScrubbers.sort(EquipmentView.compareEquirementManufacturerThenModel)
      this.dehumidifiers = this.dehumidifiers.sort(EquipmentView.compareEquirementManufacturerThenModel)
      this.floorDryingSystems = this.floorDryingSystems.sort(EquipmentView.compareEquirementManufacturerThenModel)
      this.heaters = this.heaters.sort(EquipmentView.compareEquirementManufacturerThenModel)
      console.info(`Total: ${this.equipmentList.length}  Dehus: ${this.dehumidifiers.length}`)

      this.equipmentListSnapshotCounter += 1
    } catch (error) {
      console.error(`Error Loading Equipment: ${error}`)
      appManager.alertManager.publishError('Error loading equipment. Please try again later.')
    }

    this.isLoadingEquipment = false
  }

  private static compareEquirementManufacturerThenModel(e1: Equipment, e2: Equipment): number {
    const manufacturerCompare = e1.data.manufacturer.nameLower.localeCompare(e2.data.manufacturer.nameLower)
    if (manufacturerCompare !== 0) {
      return manufacturerCompare
    }

    return e1.data.modelLower.localeCompare(e2.data.modelLower)
  }

  private onAddEquipmentPressed() {
    // TODO: complete
  }

  private onAirMoverClicked(item: Dehumidifier) {
    console.log(item.data.model)
  }

  private onDehumidifierClicked(item: Dehumidifier) {
    console.log(item.data.model)
  }

  private onFloorDryingSystemClicked(item: FloorDryingSystem) {
    console.log(item.data.model)
  }

  private getNumberAsString(num: number): string {
    return num.toLocaleString()
  }

  // return red if the calc type is different from the air mover type
  private getCalcRoomCountsTypeStyle(airMover: AirMover): string|undefined {
    if (airMover.data.canCalcRoomCounts &&
        airMover.data.calcRoomCountsType &&
        airMover.data.calcRoomCountsType !== airMover.data.airMoverType) {
      return `color: ${AppColors.secondary}`
    }

    return undefined
  }

  private getCalcRoomCountsTypeText(airMover: AirMover): string|undefined {
    if (!airMover.data.canCalcRoomCounts) {
      return undefined
    }
    if (airMover.data.calcRoomCountsType &&
        airMover.data.calcRoomCountsType !== airMover.data.airMoverType) {
      return airMover.data.calcRoomCountsType!
    }
    return airMover.data.airMoverType
  }

  private getAirScrubberSettingsRowSpan(airScrubber: AirScrubber): number {
    const count = airScrubber.data.settings.length
    return (count > 0) ? count : 1
  }

  private getHeaterSettingsRowSpan(heater: Heater): number {
    const count = heater.data.settings.length
    return (count > 0) ? count : 1
  }

  private getAllAirScrubberSettingPairs(): AirScrubberSettingPair[] {
    const pairs: AirScrubberSettingPair[] = []
    for (const airScrubber of this.airScrubbers) {
      for (const setting of airScrubber.data.settings) {
        pairs.push({
          airScrubber: airScrubber,
          airScrubberSetting: setting
        })
      }
    }
    return pairs
  }

  private getAllHeaterSettingPairs(): HeaterSettingPair[] {
    const pairs: HeaterSettingPair[] = []
    for (const heater of this.heaters) {
      for (const setting of heater.data.settings) {
        pairs.push({
          heater: heater,
          heaterSetting: setting
        })
      }
    }
    return pairs
  }

  private getEquipmentSettingTextLocal(settingsCount: number, settingsIndex: number): string {
    const text = getEquipmentSettingText(settingsCount, settingsIndex)
    return (text !== undefined) ? text : 'Unk'
  }
}
